import { useContext, useMemo } from 'react'
import GeoContext from 'contexts/geoContext'
import useLoyaltyPointsEarnCalculator from './useLoyaltyPointsEarnCalculator'
import {
  generateLoyaltyPointsCalculatorHotelOptions,
  generateLoyaltyPointsCalculatorBundledFlightOptions,
} from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'

interface Params {
  offer: App.Offer | App.OfferSummary | undefined
  packageView: App.OfferPackageOptionView | undefined
  nights: number
  /**
   * Flight price for bundled flight calculation.
   * If not provided, will use packageView.flightPrice if available.
   */
  flightPrice?: number
  skip?: boolean
}

interface PackageStatusCreditsResult {
  statusCredits: number
  isLoading: boolean
  hasError: boolean
}

function usePackageStatusCreditsCalculation(params: Params): PackageStatusCreditsResult {
  const { offer, packageView, nights, flightPrice: flightPriceProp, skip = false } = params
  const { currentRegionCode } = useContext(GeoContext)

  const flightPrice = flightPriceProp ?? (packageView?.flightPrice && packageView.flightPrice > 0 ? packageView.flightPrice : undefined)

  const calculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (skip || !offer || !packageView) {
      return [undefined, undefined]
    }

    const hotelRequest = generateLoyaltyPointsCalculatorHotelOptions(offer, {
      packageView,
      currentRegionCode,
      nights,
    })

    const flightRequest = generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      packageView,
      flightPrice,
      skip: !flightPrice,
    })

    return [hotelRequest, flightRequest]
  }, [skip, offer, packageView, currentRegionCode, nights, flightPrice])

  const { totalPointsCalculation } = useLoyaltyPointsEarnCalculator(calculationRequests, {
    skipAll: skip || !offer || !packageView,
  })

  const statusCredits = (totalPointsCalculation.data?.statusCredits ?? 0) + (totalPointsCalculation.data?.bonusStatusCredits ?? 0)
  const isLoading = !!totalPointsCalculation.initial || !!totalPointsCalculation.fetching
  const hasError = !!totalPointsCalculation.error

  return { statusCredits, isLoading, hasError }
}

export default usePackageStatusCreditsCalculation
